<template>
  <div class="relative">
    <!-- Input ultra-compacto -->
    <div class="flex items-end space-x-2">
      <div class="flex-1">
        <textarea
          ref="textareaRef"
          v-model="inputText"
          @keydown="handleKeydown"
          @input="adjustHeight"
          :disabled="disabled || isLoading"
          :placeholder="dynamicPlaceholder"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm transition-all duration-200 min-h-[40px] max-h-24"
          :class="{
            'bg-gray-50 cursor-not-allowed text-gray-500': disabled || isLoading,
            'border-red-300 focus:ring-red-500': hasError,
            'hover:border-gray-400': !disabled && !isLoading
          }"
          rows="1"
        ></textarea>
      </div>
      
      <!-- Botón de envío compacto -->
      <button
        @click="handleSend"
        :disabled="!canSend"
        class="p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 flex-shrink-0"
        :class="{
          'animate-pulse': isLoading
        }"
        title="Enviar (Enter)"
      >
        <svg v-if="!isLoading" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
        <div v-else class="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
      </button>
    </div>
    
    <!-- Información mínima -->
    <div v-if="attemptCount > 0" class="mt-1 text-xs text-center text-gray-500">
      <span v-if="attemptCount === 1" class="text-blue-600">
        💡 Sé más específico
      </span>
      <span v-else-if="attemptCount >= 2" class="text-amber-600">
        ⚠️ Próximo: ticket automático
      </span>
    </div>
    
    <!-- Sugerencias solo en primera interacción -->
    <div v-if="showSuggestions && suggestions.length > 0 && attemptCount === 0" class="mt-2">
      <div class="flex flex-wrap gap-1 justify-center">
        <button
          v-for="suggestion in suggestions"
          :key="suggestion"
          @click="applySuggestion(suggestion)"
          class="px-2 py-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-full transition-colors"
        >
          {{ suggestion }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

// Props simplificadas
const props = defineProps({
  modelValue: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  attemptCount: { type: Number, default: 0 },
  maxLength: { type: Number, default: 500 },
  showSuggestions: { type: Boolean, default: true }
});

// Emits
const emit = defineEmits(['update:modelValue', 'send', 'quick-action']);

// Refs
const textareaRef = ref(null);
const inputText = ref(props.modelValue);
const hasError = ref(false);

// Computed mejorados
const canSend = computed(() => {
  return inputText.value.trim().length > 0 && 
         inputText.value.length <= props.maxLength && 
         !props.isLoading && 
         !props.disabled;
});

const dynamicPlaceholder = computed(() => {
  if (props.attemptCount === 0) {
    return 'Describe tu problema aquí...';
  } else if (props.attemptCount === 1) {
    return 'Proporciona más detalles específicos...';
  } else {
    return 'Último intento - sé muy específico...';
  }
});

const suggestions = computed(() => {
  return [];
});

// Methods
function handleSend() {
  if (!canSend.value) return;
  
  hasError.value = false;
  emit('send');
}

function handleKeydown(event) {
  // Enviar con Enter (sin Shift)
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
}

function adjustHeight() {
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto';
      const minHeight = 48; // Altura mínima para mantener el botón centrado
      const maxHeight = 120; // Máximo 4 líneas aproximadamente
      const newHeight = Math.max(minHeight, Math.min(textareaRef.value.scrollHeight, maxHeight));
      textareaRef.value.style.height = newHeight + 'px';
    }
  });
}

function applySuggestion(suggestion) {
  inputText.value = suggestion;
  emit('update:modelValue', suggestion);
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.focus();
      adjustHeight();
    }
  });
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  inputText.value = newValue;
});

watch(inputText, (newValue) => {
  emit('update:modelValue', newValue);
  
  // Validar longitud
  if (newValue.length > props.maxLength) {
    hasError.value = true;
  } else {
    hasError.value = false;
  }
});

// Auto-ajustar altura al montar
nextTick(() => {
  adjustHeight();
});
</script>

<style scoped>
/* Animaciones y transiciones mejoradas */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: .8;
  }
}

/* Mejoras visuales para el textarea */
textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Transiciones simples para botones */
button {
  transition: background-color 0.15s ease;
}

.suggestion-button {
  transition: background-color 0.15s ease;
}

/* Mejora para estados de error */
.border-red-300 {
  border-color: #fca5a5;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}

/* Scroll suave para textarea */
textarea {
  scroll-behavior: smooth;
}

/* Estados de carga más sutiles */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
